/**
 * Inline styles so tag choice previews read as real pills (full background + legible text).
 * Auto foreground contrast uses hex (#rgb / #rrggbb) and rgb()/rgba() literals only; other
 * CSS color strings fall back to the default dark text (no DOM resolution).
 */

export type ChoicePillInlineStyle = {
  backgroundColor: string;
  color: string;
};

function parseHexRgb(input: string): [number, number, number] | null {
  let s = input.trim();
  if (s.startsWith('#')) s = s.slice(1);
  if (/^[0-9a-f]{3}$/i.test(s)) {
    return [parseInt(s[0]! + s[0]!, 16), parseInt(s[1]! + s[1]!, 16), parseInt(s[2]! + s[2]!, 16)];
  }
  if (/^[0-9a-f]{6}$/i.test(s)) {
    return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
  }
  return null;
}

function parseRgbFunctionString(input: string): [number, number, number] | null {
  const m = input.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

function resolveBackgroundToRgb(cssColor: string): [number, number, number] | null {
  const hex = parseHexRgb(cssColor);
  if (hex) return hex;
  const rgbLiteral = parseRgbFunctionString(cssColor);
  if (rgbLiteral) return rgbLiteral;
  return null;
}

function relativeLuminance(r: number, g: number, b: number): number {
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const R = lin(r);
  const G = lin(g);
  const B = lin(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

function contrastingForeground(backgroundCss: string): string {
  const rgb = resolveBackgroundToRgb(backgroundCss);
  if (!rgb) return '#171717';
  const L = relativeLuminance(rgb[0], rgb[1], rgb[2]);
  return L > 0.45 ? '#171717' : '#fafafa';
}

const NEUTRAL_PILL_BG = '#f3f4f6';
const NEUTRAL_PILL_FG = '#171717';

/**
 * Full pill background + text (and icon) color. Uses explicit textColor when set;
 * otherwise picks light or dark foreground from the background for readability.
 */
export function choicePillInlineStyles(
  color: string | undefined,
  textColor: string | undefined,
): ChoicePillInlineStyle {
  const explicit = textColor?.trim();
  const bg = color?.trim();
  if (!bg) {
    return {
      backgroundColor: NEUTRAL_PILL_BG,
      color: explicit ?? NEUTRAL_PILL_FG,
    };
  }
  return {
    backgroundColor: bg,
    color: explicit ?? contrastingForeground(bg),
  };
}
